import React from "react";
import PropTypes from "prop-types";
import { FormField, Input, Toggle } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { DEFAULT_PRIORITY } from "./automationUtils";

/**
 * Per-automation delivery policy flags: priority (higher wins when the global
 * frequency cap binds), "ignore global frequency caps" and "respect quiet hours".
 */
const DeliveryPolicyFields = ({
  priority = String(DEFAULT_PRIORITY),
  ignoreFrequencyCaps = false,
  respectQuietHours = true,
  onChange,
  disabled = false,
  errors = {},
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField label="Priority" htmlFor="automation-priority">
          <Input
            id="automation-priority"
            type="number"
            inputMode="numeric"
            step={1}
            value={priority === null || priority === undefined ? "" : String(priority)}
            onChange={(e) => onChange({ priority: e.target.value })}
            disabled={disabled}
            error={errors?.priority || ""}
            helperText={`Higher wins when a user hits the global frequency cap and several automations compete for the same slot. Default ${DEFAULT_PRIORITY}.`}
            placeholder={String(DEFAULT_PRIORITY)}
          />
        </FormField>
      </div>

      <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} divide-y ${borders.light}`}>
        <div className="p-4">
          <Toggle
            id="automation-ignore-caps"
            checked={Boolean(ignoreFrequencyCaps)}
            onChange={(e) => onChange({ ignore_frequency_caps: Boolean(e?.target?.checked) })}
            label="Ignore global frequency caps"
            description="Bypass the rolling 7 / 10 / 30-day caps from Global delivery policy. Reserve this for messages that must go out even to users who already received a lot recently."
            labelPosition="right"
            disabled={disabled}
            className="w-full"
          />
          {ignoreFrequencyCaps ? (
            <p className={`mt-2 ${typography.caption} text-yellow-700 dark:text-yellow-300`}>
              Caps are ignored — this automation can message users who are already at their limit.
            </p>
          ) : null}
        </div>
        <div className="p-4">
          <Toggle
            id="automation-respect-quiet-hours"
            checked={Boolean(respectQuietHours)}
            onChange={(e) => onChange({ respect_quiet_hours: Boolean(e?.target?.checked) })}
            label="Respect quiet hours"
            description="Emails that would land inside the recipient's quiet hours (recipient-local, set in Global delivery policy) are held until the window ends. Inbox messages are never delayed."
            labelPosition="right"
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>

      <p className={`${typography.caption} ${textColors.tertiary}`}>
        Suppressed email addresses (bounces / complaints) and marketing opt-outs are always honoured, regardless of
        these flags.
      </p>
    </div>
  );
};

DeliveryPolicyFields.propTypes = {
  priority: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ignoreFrequencyCaps: PropTypes.bool,
  respectQuietHours: PropTypes.bool,
  /** (patch) — { priority } | { ignore_frequency_caps } | { respect_quiet_hours } */
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  errors: PropTypes.object,
};

export default DeliveryPolicyFields;
